import React, {useEffect, useMemo} from 'react'
import PageLayout from '../../components/layout/PageLayout'
import Text from '../../components/text/Text'
import WizButton from '../../components/button/WizButton'
import {useTime} from '../../utils/time'
import {useDisplayNumber} from '../../utils/number'
import useQueryExchanges from '../../hooks/etc/useQueryExchanges'
import useQueryVolumeLeagues from '../../hooks/api/volume_league/useQueryVolumeLeagues'
import {IVolumeLeague, LeagueKind, LeagueStatus, LeagueVisibility} from '../../apis/volume_league/list'
import {editVolumeLeague} from '../../apis/volume_league/edit'
import {deleteVolumeLeague} from '../../apis/volume_league/delete'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import StatusFilter from './StatusFilter'
import GroupFilter from './GroupFilter'
import GroupHubList from './GroupHubList'
import LeagueCardList from './LeagueCardList'
import QuickSortPanel from './QuickSortPanel'
import LeagueForm from './LeagueForm'
import {ICard, IFormBody, IGroup} from './types'
import {FIXED_WINDOWS, STATUS_META, STATUS_ORDER} from './constants'
import {groupOf, marketsToText, stripTf} from './utils'

/**
 * Volume League — 관리 (개선 테스트)
 * 기존 Competition / Rolling 페이지를 하나로 병합한 통합 운영 화면.
 *  - 목록(허브): 종류·대상 무관 한 목록. rolling 은 같은 대상의 기간 세트(24H/7D/30D…)를 한 줄로 묶어 표시.
 *  - 생성/수정 폼: ① 종류(상시 순위표=rolling / 기간 대회=competition) → ② 집계 대상(코인/거래소) → ③ 세부 설정
 *  - 상시 순위표는 선택한 기간들을 한 번에 여러 레코드로 생성 (세트).
 *
 * 이 파일은 "허브 화면의 뼈대 + 공유 상태"만 담당한다.
 *  - 공유 상태: view / statusFilter / groupFilter / body(폼 데이터) / 삭제 대상 / 그룹 순서·정렬 저장
 *  - 화면 파츠별 전용 로직은 각 컴포넌트가 직접 소유:
 *      · StatusFilter / GroupFilter : 필터 칩
 *      · GroupHubList : 허브 목록 + 드래그 시각 상태(onReorder 만 위임)
 *      · LeagueCardList : 그룹 진입 후 카드 목록
 *      · QuickSortPanel : sort_value 빠른 변경 (입력/저장 상태 자체 소유)
 *      · LeagueForm : 생성/수정 폼 (검증·핸들러·제출·링크 경고 모달 자체 소유)
 *  body 는 폼 마운트 전에 set 되고 카드 하이라이트/삭제에서도 읽혀 이 파일에 유지한다.
 */

interface IProps {
    className?: string
}

export function VolumeLeague({className}: IProps) {
    const {data: leagues, refetch} = useQueryVolumeLeagues()
    const {data: exchanges} = useQueryExchanges()
    const {displayAmount} = useDisplayNumber()
    const {formatUTCDate, convertInputToTimezone, convertTimezoneToInput} = useTime()

    const [view, setView] = React.useState<'list' | 'create' | 'edit'>('list')
    const [statusFilter, setStatusFilter] = React.useState<'all' | LeagueStatus>('all')
    // 순서 저장 진행 중 (리스트 위 dimmed 로딩)
    const [reordering, setReordering] = React.useState(false)

    const allLeagues = useMemo<IVolumeLeague[]>(() => leagues || [], [leagues])

    // 카드 키 — 레코드 1개 = 행 1개 (24H/7D/30D 도 개별 아이템으로 취급). 그룹핑은 groupOf 로 별도 처리.
    const cardKey = (l?: IVolumeLeague): string => (l ? `${l.id}` : '-')

    // 거래소 로고 (admin 모델엔 logo 없음 → exchange 매칭 or 심볼 캐시 URL)
    const exchangeMeta = (name?: string | null) =>
        exchanges?.find(ex => (ex?.name || '').toLowerCase() === (name || '').toLowerCase())
    const cardLogo = (l?: IVolumeLeague): string => {
        if (!l) return ''
        if (l?.scope_type === 'exchange') return exchangeMeta(l?.exchange)?.icon || exchangeMeta(l?.exchange)?.logo || ''
        // symbol scope: 캐시 심볼 로고 (실패 시 placeholder 로 폴백)
        const sym = (l?.symbol || '').toUpperCase()
        return sym ? `https://cache.leagueoftraders.io/${sym}.png` : ''
    }

    // 카드(=행) 목록. sort_value 내림차순, 동률은 최신(id) 우선.
    const cards = useMemo<ICard[]>(() => {
        const map = new Map<string, IVolumeLeague[]>()
        allLeagues.forEach(l => {
            const k = cardKey(l)
            if (!map.has(k)) map.set(k, [])
            map.get(k)!.push(l)
        })
        const list: ICard[] = Array.from(map.entries()).map(([key, items]) => {
            const rep = [...items].sort((a, b) => (a?.window_days ?? 0) - (b?.window_days ?? 0))[0]
            return {
                key,
                group: groupOf(rep),
                rep,
                items,
                sortValue: items.reduce((m, l) => Math.max(m, l?.sort_value ?? 0), 0),
                maxId: items.reduce((m, l) => Math.max(m, l?.id ?? 0), 0),
            }
        })
        return list.sort((a, b) => b.sortValue - a.sortValue || b.maxId - a.maxId)
    }, [allLeagues, exchanges])

    // 그룹(거래소/토큰) 단위 묶음 — 필터·드래그·sort_value 통일의 기준
    const groups = useMemo<IGroup[]>(() => {
        const map = new Map<string, ICard[]>()
        cards.forEach(c => {
            if (!map.has(c.group)) map.set(c.group, [])
            map.get(c.group)!.push(c)
        })
        const arr = Array.from(map.entries()).map(([group, cs]) => ({
            group,
            cards: cs,
            sortValue: cs.reduce((m, c) => Math.max(m, c.sortValue), 0),
            maxId: cs.reduce((m, c) => Math.max(m, c.maxId), 0),
        }))
        return arr.sort((a, b) => b.sortValue - a.sortValue || b.maxId - a.maxId)
    }, [cards])

    // 그룹 필터 (상태 필터와 별개). 'all' = 전체 그룹
    const [groupFilter, setGroupFilter] = React.useState<string>('all')

    // 필터 칩 개수 (상태=레코드 단위, 그룹=그룹 단위)
    const counts = useMemo(() => {
        const c: {[k: string]: number} = {all: allLeagues.length}
        STATUS_ORDER.forEach(s => (c[s] = allLeagues.filter(l => l?.status === s).length))
        return c
    }, [allLeagues])

    // 그룹 칩 — 현재 상태 필터와 매칭되는 리그 수만 카운트. 매칭 0인 그룹은 칩에서 제외.
    const groupChips = useMemo(() => {
        return groups
            .map(g => ({
                group: g.group,
                count: g.cards.reduce(
                    (m, c) => m + c.items.filter(l => statusFilter === 'all' || l?.status === statusFilter).length,
                    0,
                ),
            }))
            .filter(g => g.count > 0)
    }, [groups, statusFilter])

    // ---------------- 드래그 정렬 (그룹 단위, 전체 상태·전체 그룹에서만) ----------------
    // 드래그 시각 상태(끌고 있는 그룹/드롭 위치)는 GroupHubList 가 소유. 여기선 순서 데이터와 저장만 담당.
    const dragEnabled = statusFilter === 'all' && groupFilter === 'all'
    const [localGroupOrder, setLocalGroupOrder] = React.useState<string[]>([])
    useEffect(() => {
        setLocalGroupOrder(groups.map(g => g.group))
    }, [groups])

    // 화면에 표시할 그룹 순서 (드래그 가능 시 localGroupOrder, 아니면 정렬 그대로) + 그룹 필터 적용
    //  - 허브(전체)는 종류 무관 모든 그룹 표시 (기간 대회만 있는 그룹 포함)
    const orderedGroups = useMemo<IGroup[]>(() => {
        const byKey = new Map(groups.map(g => [g.group, g]))
        let base: IGroup[] = dragEnabled ? (localGroupOrder.map(k => byKey.get(k)).filter(Boolean) as IGroup[]) : groups
        if (dragEnabled) {
            groups.forEach(g => {
                if (!base.find(b => b.group === g.group)) base = [...base, g]
            })
        }
        if (groupFilter !== 'all') {
            base = base.filter(g => g.group === groupFilter)
        }
        return base
    }, [dragEnabled, groups, localGroupOrder, groupFilter])

    // 렌더용 카드 목록 (그룹 순서대로 평탄화 + 상태 필터). 같은 그룹은 인접하게.
    //  - 그룹 필터가 적용된 목록은 "기간 대회 → 상시 순위표" 순.
    //  - 기간 대회끼리는 end_at 이 가장 먼 항목부터 위에 노출.
    //  - 상시 순위표끼리는 window_days 짧은 순(24H → 7D → 30D …).
    const orderedCards = useMemo<ICard[]>(() => {
        const out: ICard[] = []
        orderedGroups.forEach(g => {
            const cs = [...g.cards].sort((a, b) => {
                const ak = a.rep?.kind === 'competition' ? 0 : 1
                const bk = b.rep?.kind === 'competition' ? 0 : 1
                if (ak !== bk) return ak - bk
                // 기간 대회끼리: end_at 이 먼(미래) 항목 먼저
                if (ak === 0) return new Date(b.rep?.end_at || 0).getTime() - new Date(a.rep?.end_at || 0).getTime()
                // 상시 순위표끼리: window_days 짧은 순
                return (a.rep?.window_days ?? 0) - (b.rep?.window_days ?? 0)
            })
            cs.forEach(c => {
                if (statusFilter === 'all' || c.items.some(l => l?.status === statusFilter)) out.push(c)
            })
        })
        return out
    }, [orderedGroups, statusFilter])

    // 그룹 단위 드래그 결과 적용 → sort_value 를 그룹별로 통일해 저장.
    // 한꺼번에 처리하면 항목이 많아 실패할 수 있어 "그룹별로 진행 → 성공 → 다음 그룹" 순차 처리.
    const onReorder = async (from: string, targetGroup: string) => {
        if (!from || from === targetGroup) return
        const next = [...localGroupOrder]
        const fromIdx = next.indexOf(from)
        const toIdx = next.indexOf(targetGroup)
        if (fromIdx === -1 || toIdx === -1) return
        next.splice(toIdx, 0, next.splice(fromIdx, 1)[0])
        setLocalGroupOrder(next)

        // 위일수록 큰 sort_value. 값이 바뀐 그룹만 추려서 순서대로 처리.
        const byKey = new Map(groups.map(g => [g.group, g]))
        const total = next.length
        const changed: {group: string; sort: number; ids: number[]}[] = []
        next.forEach((g, idx) => {
            const grp = byKey.get(g)
            if (!grp) return
            const newSort = total - idx
            if (grp.sortValue !== newSort) {
                const ids = grp.cards.reduce<number[]>((acc, c) => acc.concat(c.items.map(l => l.id)), [])
                changed.push({group: g, sort: newSort, ids})
            }
        })
        if (!changed.length) return

        setReordering(true)
        try {
            // 그룹별 순차: 한 그룹(내부 항목 일괄)을 성공시킨 뒤 다음 그룹으로
            for (const ch of changed) {
                await Promise.all(ch.ids.map(id => editVolumeLeague({id, sort_value: ch.sort})))
            }
            await refetch()
        } finally {
            setReordering(false)
        }
    }

    // 상태 변경 (세트면 전 레코드 일괄)
    const changeStatus = async (card: ICard, status: LeagueStatus) => {
        await Promise.all(card.items.map(l => editVolumeLeague({id: l.id, status})))
        refetch()
    }

    // ---------------- 폼 데이터 (공유) ----------------
    // body 는 폼이 마운트되기 전 openCreate/openEdit 에서 set 되고, body.editKey 가 카드 목록
    // 하이라이트/삭제에서도 쓰이므로 폼 안이 아니라 이 파일에서 보관한다. (검증·제출은 LeagueForm 소유)
    const resetValue = useMemo<IFormBody>(
        () => ({
            editKey: '', // 수정 중인 카드 key (빈 값 = 생성)
            editIds: [] as number[], // 수정 대상 레코드 id 들
            kind: 'rolling' as LeagueKind,
            scope_type: 'symbol',
            symbol: '',
            exchange: '',
            baseName: '',
            windows: [...FIXED_WINDOWS] as number[], // rolling 기간 세트
            supported_exchanges: [] as string[],
            exchange_urls: {} as {[exchange: string]: string}, // 집계 거래소별 링크 (symbol scope, 1:1 매칭)
            status: 'draft' as LeagueStatus,
            prizeAmount: 0,
            prizeCurrency: '',
            start_at: '',
            end_at: '',
            refresh_interval_sec: 600,
            url_third_party: '',
            sort_value: 0,
            slot_id: {ko: '', en: ''},
            mobile_slot_id: {ko: '', en: ''},
            visibility: 'public' as LeagueVisibility,
            marketsText: '',
        }),
        [],
    )
    const [body, setBody] = React.useState<IFormBody>(resetValue)

    const globalMaxSort = useMemo(() => allLeagues.reduce((m, l) => Math.max(m, l?.sort_value ?? 0), 0), [allLeagues])

    // 그룹 필터가 적용된 화면(폼 닫힘)에서 QuickSortPanel 에 넘길 현재 그룹
    const currentGroup = useMemo(() => groups.find(g => g.group === groupFilter), [groups, groupFilter])

    const openCreate = () => {
        const base = {
            ...resetValue,
            kind: 'rolling' as LeagueKind,
            refresh_interval_sec: 600,
            sort_value: globalMaxSort + 1,
        }
        // 특정 그룹이 선택된 상태면, 그 그룹의 기존 리그 기준으로 폼을 미리 채운다 (이미 1개 이상 존재하므로)
        if (groupFilter !== 'all') {
            const grp = groups.find(g => g.group === groupFilter)
            const rollingRep = grp?.cards.find(c => c.rep?.kind === 'rolling')?.rep
            const rep = rollingRep || grp?.cards[0]?.rep
            if (rep) {
                setBody({
                    ...base,
                    scope_type: rep?.scope_type || 'symbol',
                    symbol: rep?.scope_type === 'symbol' ? (rep?.symbol || '').toUpperCase() : '',
                    exchange: rep?.scope_type === 'exchange' ? rep?.exchange || '' : '',
                    // 기간 이름은 기존 rolling 이름의 기준부(예: "Tenbagger 24H" → "Tenbagger"), 없으면 그룹 라벨
                    baseName: rollingRep
                        ? stripTf(rollingRep.name) || grp?.group || ''
                        : rep?.group_label || grp?.group || '',
                    supported_exchanges: rep?.supported_exchanges || [],
                    exchange_urls: rep?.exchange_urls || {},
                    refresh_interval_sec: rep?.refresh_interval_sec ?? 600,
                    url_third_party: rep?.url_third_party || '',
                    sort_value: grp?.sortValue ?? base.sort_value, // 같은 그룹 정렬값으로 통일
                    slot_id: {ko: rep?.slot_id?.ko || '', en: rep?.slot_id?.en || ''},
                    mobile_slot_id: {ko: rep?.mobile_slot_id?.ko || '', en: rep?.mobile_slot_id?.en || ''},
                    visibility: rep?.visibility || 'public',
                    marketsText: rep?.scope_type === 'markets' ? marketsToText(rep?.markets) : '',
                })
                setView('create')
                return
            }
        }
        setBody(base)
        setView('create')
    }

    const openEdit = (card: ICard) => {
        const rep = card.rep
        setBody({
            editKey: card.key,
            editIds: card.items.map(l => l.id),
            kind: rep?.kind || 'rolling',
            scope_type: rep?.scope_type || 'symbol',
            symbol: (rep?.symbol || '').toUpperCase(),
            exchange: rep?.exchange || '',
            baseName: stripTf(rep?.name) || rep?.name || '',
            windows: card.items.map(l => l?.window_days ?? 0).filter(Boolean),
            supported_exchanges: rep?.supported_exchanges || [],
            exchange_urls: rep?.exchange_urls || {},
            status: rep?.status || 'draft',
            prizeAmount: rep?.prize_pool?.total_amount || 0,
            prizeCurrency: rep?.prize_pool?.currency || '',
            start_at: rep?.start_at || '',
            end_at: rep?.end_at || '',
            refresh_interval_sec: rep?.refresh_interval_sec ?? (rep?.kind === 'competition' ? 60 : 600),
            url_third_party: rep?.url_third_party || '',
            sort_value: card.sortValue,
            slot_id: {ko: rep?.slot_id?.ko || '', en: rep?.slot_id?.en || ''},
            mobile_slot_id: {ko: rep?.mobile_slot_id?.ko || '', en: rep?.mobile_slot_id?.en || ''},
            visibility: rep?.visibility || 'public',
            marketsText: rep?.scope_type === 'markets' ? marketsToText(rep?.markets) : '',
        })
        setView('edit')
    }

    // 삭제 확인 모달 대상 + 진행 상태
    const [deleteTarget, setDeleteTarget] = React.useState<ICard | null>(null)
    const [deleting, setDeleting] = React.useState(false)

    const performDelete = async () => {
        const card = deleteTarget
        if (!card) return
        setDeleting(true)
        try {
            const results = await Promise.all(card.items.map(l => deleteVolumeLeague(l.id)))
            if (results.every(r => r?.status === 200)) {
                if (body.editKey === card.key) setView('list')
                setDeleteTarget(null)
                await refetch()
            } else {
                alert('일부 삭제에 실패했습니다.')
            }
        } finally {
            setDeleting(false)
        }
    }

    // ---------------- 렌더 ----------------
    return (
        <PageLayout title={'Volume League — 관리 (개선 테스트)'} className={className}>
            <div className="flex items-center gap-x-[14px]">
                <WizButton text={'새 리그 만들기'} type="primary" onClick={openCreate} />
            </div>

            {/* 상태 필터 */}
            <StatusFilter
                statusFilter={statusFilter}
                counts={counts}
                onSelect={s => {
                    setStatusFilter(s)
                    setView('list')
                }}
            />

            {/* 그룹 필터 (거래소/토큰) */}
            <GroupFilter
                groupFilter={groupFilter}
                groupChips={groupChips}
                totalGroups={orderedGroups?.length}
                onSelect={g => {
                    setGroupFilter(g)
                    setView('list')
                }}
            />

            <div className="mt-[20px] grid grid-cols-[1.6fr_1px_2fr] gap-x-[48px]">
                {/* 목록 */}
                <div className="relative">
                    {/* 순서 저장 중 dimmed 로딩 (리스트 위) */}
                    {reordering && (
                        <div className="absolute inset-0 z-20 bg-white/70 flex items-center justify-center rounded-[6px]">
                            <div className="flex items-center gap-x-[10px]">
                                <div className="w-[20px] h-[20px] rounded-full border-2 border-line02 border-t-sub_blue animate-spin" />
                                <Text className="text-ti1 text-font01">순서 저장 중…</Text>
                            </div>
                        </div>
                    )}
                    <article className="space-y-[14px] pr-[8px]">
                        {groupFilter === 'all' ? (
                            <GroupHubList
                                groups={orderedGroups}
                                dragEnabled={dragEnabled}
                                onReorder={onReorder}
                                onEnterGroup={g => {
                                    setGroupFilter(g)
                                    setView('list')
                                }}
                                cardLogo={cardLogo}
                            />
                        ) : (
                            <LeagueCardList
                                cards={orderedCards}
                                editKey={body.editKey}
                                onEdit={openEdit}
                                onChangeStatus={changeStatus}
                                onDelete={card => setDeleteTarget(card)}
                                cardLogo={cardLogo}
                                formatUTCDate={formatUTCDate}
                                displayAmount={displayAmount}
                            />
                        )}
                    </article>
                </div>

                {/* 폼이 닫혀 있고 그룹 필터가 적용된 상태 — sort_value 빠른 변경 패널 */}
                {view === 'list' && groupFilter !== 'all' && currentGroup && (
                    <QuickSortPanel group={currentGroup} globalMaxSort={globalMaxSort} onSaved={refetch} />
                )}

                {/* 생성/수정 폼 */}
                {view !== 'list' && (
                    <LeagueForm
                        view={view as 'create' | 'edit'}
                        body={body}
                        setBody={setBody}
                        exchanges={exchanges}
                        allLeagues={allLeagues}
                        cards={cards}
                        exchangeMeta={exchangeMeta}
                        convertTimezoneToInput={convertTimezoneToInput}
                        convertInputToTimezone={convertInputToTimezone}
                        onSubmitted={() => {
                            setView('list')
                            refetch()
                        }}
                        onRequestDelete={() => {
                            const card = cards.find(c => c.key === body.editKey)
                            if (card) setDeleteTarget(card)
                        }}
                    />
                )}
            </div>

            {/* 삭제 확인 모달 (dimmed) */}
            <ConfirmDeleteModal
                open={!!deleteTarget}
                loading={deleting}
                title={'리그를 삭제할까요?'}
                name={deleteTarget?.rep?.name}
                rows={
                    deleteTarget
                        ? [
                              {
                                  label: '종류',
                                  value: deleteTarget.rep?.kind === 'rolling' ? '상시 순위표' : '기간 대회',
                              },
                              {
                                  label: '집계 대상',
                                  value:
                                      deleteTarget.rep?.scope_type === 'exchange'
                                          ? `거래소 기준 · ${(deleteTarget.rep?.exchange || '').toUpperCase()}`
                                          : `코인 기준 · ${(deleteTarget.rep?.symbol || '').toUpperCase()}`,
                              },
                              {label: '그룹', value: deleteTarget.group},
                              {
                                  label: '상태',
                                  value: STATUS_META[(deleteTarget.rep?.status as LeagueStatus) || 'draft'].label,
                              },
                              {label: '삭제될 항목', value: `${deleteTarget.items.length}개`},
                          ]
                        : []
                }
                note={'리그와 라이브 보드가 함께 삭제되며 복구할 수 없습니다.'}
                warning={
                    deleteTarget?.items.some(l => l?.status === 'active')
                        ? '현재 진행 중인 리그입니다. 정말 삭제하시겠습니까?'
                        : undefined
                }
                onConfirm={performDelete}
                onCancel={() => !deleting && setDeleteTarget(null)}
            />
        </PageLayout>
    )
}

export default VolumeLeague
